import { Formik } from 'formik';
import qs from 'qs';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { LocalizedService, RNDService, useLocalizedServiceFactory } from '../../../API/GoMoto';
import { ServiceCategory } from '../../../API/Enums';
import { SpgPriceText } from '../../../controls/SpgPriceText/SpgPriceText';
import { useAppointmentState, useAppointmentUpdateAndPersist, useHasAppointment } from '../../../context/Appointment/appointment';
import { Types, useCart, useCartDispatch } from '../../../context/cart';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useHideKioskBackground } from '../../../context/Page/page';
import { useBackgroundDataState } from '../../../context/backgroundData';
import { CTAButton } from '../../../controls/ButtonBase/index';
import { Disclaimer } from '../../../controls/Disclaimer/Disclaimer';
import { KeyboardLayouts } from '../../../controls/SimpleKeyboard/Layout';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { reverseEnum } from '../../../helpers/enum';
import css from './index.module.scss';
import { MaintenanceSchedules } from './MaintenanceSchedules';
import { Tab, Tabs } from '../../../controls/Tabs/UncontrolledTabs';
import { useTabFocusRefContext } from '../../../controls/Tabs/Tabs';
import { getUpdateAptRndServices, getUpdateAptServices } from '../../../helpers/Appointment';
import { ServiceCard, ServiceCardList } from '../../../controls/ServiceCard/ServiceCard';
import { isElectron } from '../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { nameof } from '../../../helpers/nameof';
import { GMFormTextArea } from '../../../controls/TextField';
import { GMDialogError } from '../../../controls/Dialog';
import { GMFormSelect, GMMenuItem } from '../../../controls/Select';
import { GMForm } from '../../../controls/Form';

export interface IProps {
    onProceed: () => void;
}

const reversedServiceCategory = reverseEnum(ServiceCategory);
interface IAvailableCategory {
    category: ServiceCategory;
    display: string;
}

export const StandardServices: React.FC<IProps> = React.memo((props) => {
    useHideKioskBackground()();
    const location = useLocation();
    const Localization = useLocalization();
    const serviceFactory = useLocalizedServiceFactory();
    const appointment = useAppointmentState();
    const hasAppointment = useHasAppointment();
    const {
        dealer: { make, maintenanceMenusEnabled, xtimeEnabled },
    } = useKioskState();
    const cartDispatch = useCartDispatch();
    const cart = useCart();
    const { standardServices, menusInitialized, loadingMenus, mileageOptions, scheduleOptions, menus, rndServices, rndServicesInitialized } =
        useBackgroundDataState();
    const [allServices, setAllServices] = React.useState<LocalizedService[]>([]);
    const [serviceRequiredError, setServiceRequiredError] = React.useState(false);

    const [availableCategories, setAvailableCategories] = React.useState<IAvailableCategory[]>([
        {
            category: ServiceCategory.All,
            display: Localization.getString(reversedServiceCategory[ServiceCategory.All], Localization.getLanguage()) || reversedServiceCategory[ServiceCategory.All],
        },
    ]);
    const updateAppointment = useAppointmentUpdateAndPersist();
    const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
    const showMaintenanceScheduleTab: boolean =
        maintenanceMenusEnabled &&
        !xtimeEnabled &&
        (menus.length > 0 ||
            mileageOptions.length > 0 ||
            scheduleOptions.length > 0 ||
            (menus.length === 0 && mileageOptions.length === 0 && scheduleOptions.length === 0 && loadingMenus));
    const [activeTabIndex, setActiveTabIndex] = React.useState<number>(showMaintenanceScheduleTab ? 0 : parsed.showCommentsTab === 'true' ? 1 : 0);
    const isKiosk = isElectron();

    React.useEffect(() => {
        /**
         * Attemps to add the category to the map if it does not already exist.
         * @param category The service category to add.
         * @param map The map to which the category should be added.
         */
        const addCategoryToMap = (category: ServiceCategory, map: Map<ServiceCategory, IAvailableCategory>) => {
            if (!map.has(category)) {
                map.set(category, getLocalizedCategory(category));
            }
        };

        /**
         * Gets an IAvailableCategory to use in the category map.
         * @param category The service category to localize.
         */
        const getLocalizedCategory = (category: ServiceCategory) => ({
            category,
            display: Localization.getString(reversedServiceCategory[category], Localization.getLanguage()) || reversedServiceCategory[category],
        } as IAvailableCategory);

        if (standardServices && standardServices.length) {
            const filtered: LocalizedService[] = [];
            const localizedCategories = new Map<ServiceCategory, IAvailableCategory>();
            const orderedServices = standardServices
                .map(serviceFactory)
                .sort((elem1, elem2) => elem1.localization.name.localeCompare(elem2.localization.name, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }));
            addCategoryToMap(ServiceCategory.All, localizedCategories);
            orderedServices.forEach(function (service) {
                filtered.push(service);
                const { category } = service;
                const { isCommon, isCommonRepair } = service;
                isCommon && addCategoryToMap(ServiceCategory.Common, localizedCategories);
                isCommonRepair && addCategoryToMap(ServiceCategory.Common_Repairs, localizedCategories);
                addCategoryToMap(category, localizedCategories);
            });
            setAllServices(filtered);
            // Categories must be sorted [All, Common ...Rest, Other];
            const sorted: { category: ServiceCategory; display: string; }[] = [localizedCategories.get(ServiceCategory.All)!];

            if (localizedCategories.has(ServiceCategory.Common)) {
                sorted.push(localizedCategories.get(ServiceCategory.Common)!);
            }
            if (localizedCategories.has(ServiceCategory.Common_Repairs)) {
                sorted.push(localizedCategories.get(ServiceCategory.Common_Repairs)!);
            }
            Array.from(localizedCategories.values())
                .filter(
                    ({ category }) =>
                        category !== ServiceCategory.All &&
                        category !== ServiceCategory.Common &&
                        category !== ServiceCategory.Common_Repairs &&
                        category !== ServiceCategory.Other,
                )
                .sort((a, b) => a.display.localeCompare(b.display))
                .forEach((category) => sorted.push(category!));
            if (localizedCategories.has(ServiceCategory.Other)) {
                sorted.push(localizedCategories.get(ServiceCategory.Other)!);
            }
            setAvailableCategories(sorted);
        }
    }, [standardServices, make, Localization, serviceFactory]);

    React.useEffect(() => {
        if (!(activeTabIndex === 0 || (showMaintenanceScheduleTab && activeTabIndex === 1))) {
            cartDispatch({ type: Types.ClearSnackbar });
        }
    }, [activeTabIndex, cartDispatch, showMaintenanceScheduleTab]);

    const renderServiceItems = (category: ServiceCategory) => {
        let services: typeof allServices = [...allServices];

        if (category !== ServiceCategory.All) {
            services = services.filter(
                (service) =>
                    (category === ServiceCategory.Common && service.isCommon) ||
                    (category === ServiceCategory.Common_Repairs && service.isCommonRepair) ||
                    service.category === category,
            );
        }

        return (
            services.length > 0 &&
            services.map((service) => {
                let checked: boolean = !!(cart.services.findIndex((s) => service.id === s.id) > -1);
                const rndService: RNDService | undefined = rndServices.find((rnd) => rnd.opCode === service.opCode);
                const pricedSPGService = standardServices.find((s) => service.opCode === s.opCode);

                if (rndService) {
                    checked = checked || cart.rndServices.findIndex((cartService) => rndService.opCode === cartService.opCode && !cartService.doneElsewhere) > -1;
                }

                return (
                    <ServiceCard
                        key={service.id}
                        onClick={() => {
                            if (rndService) {
                                cartDispatch({
                                    type: Types.ToggleRNDService,
                                    payload: {
                                        ...rndService,
                                        doneElsewhere: false,
                                    },
                                });
                            }
                            else {
                                cartDispatch({
                                    type: Types.ToggleService,
                                    payload: service.full,
                                });
                            }
                        }}
                        priceDetails={service.localization.priceDetails}
                        name={service.localization.name}
                        description={service.localization.description}
                        checked={checked}
                        price={
                            rndService ? (
                                <>
                                    <SpgPriceText
                                        price={rndService.price ?? rndService.linkedService?.price}
                                        priceLow={rndService.linkedService?.priceLow}
                                        priceHigh={rndService.linkedService?.priceHigh}
                                        spgPricing={rndService.linkedService?.useSpgPricing ? rndService.linkedService?.spgPricing : undefined}
                                    />
                                    {!!rndService.price && rndService.price < (pricedSPGService?.priceLow ?? pricedSPGService?.priceHigh ?? pricedSPGService?.price ?? 0) ? (
                                        <SpgPriceText
                                            price={pricedSPGService?.price}
                                            priceLow={pricedSPGService?.priceLow}
                                            priceHigh={pricedSPGService?.priceHigh}
                                            spgPricing={pricedSPGService?.useSpgPricing ? pricedSPGService?.spgPricing : undefined}
                                            markOut={true}
                                        />
                                    ) : null}
                                </>
                            ) : (
                                <SpgPriceText
                                    price={service.price}
                                    priceLow={service.priceLow}
                                    priceHigh={service.priceHigh}
                                    spgPricing={service.spgPricing}
                                    useSpgPricingLogic={!!service.useSpgPricing}
                                />
                            )
                        }
                    />
                );
            })
        );
    };

    const onProceed = async ({ comments }: { comments: string; }) => {
        const tabMax: number = showMaintenanceScheduleTab ? 2 : 1;

        if (activeTabIndex !== tabMax) {
            setActiveTabIndex(activeTabIndex + 1);
            /*
             * hacky way to scroll to the top of the next tab when clicking Proceed button
             * (and ScrollableBody element explicitly prevents `scrollTop = 0` from working)
             */
            document.querySelector('div[class^=ScrollableBody]')!.scrollTop = 1;
        }
        else {
            const serviceComments = cart.comments.filter((c) => c.key === 'Services')
                .flatMap((c) => c.comment.split(/\s*;\s*/)
                    .map((c) => c.trim())
                    .filter((c) => !!c));
            const nonServiceComments = cart.comments.filter((c) => c.key !== 'Services')
                .flatMap((c) => c.comment.split(/\s*;\s*/)
                    .map((c) => c.trim())
                    .filter((c) => !!c));
            const initialComments = initialValues.comments.split(/\s*;\s*/)
                .map((c) => c.trim())
                .filter((c) => !!c);
            const typedComments = comments.split(/\s*;\s*/)
                .map((c) => c.trim())
                .filter((c) => !!c);

            const allComments = !isKiosk && cart.comments?.length !== 0
                ? Array.from(new Set(serviceComments.concat(nonServiceComments)
                    .concat(typedComments)
                    .concat(initialComments)))
                    .join('; ')
                : comments;
            cartDispatch({
                type: Types.UpdateComment,
                payload: {
                    key: 'Services',
                    comment: allComments,
                },
            });
            const updated: typeof appointment = {
                ...appointment,
                servicesConfirmed: getUpdateAptServices(cart.services),
                rndServices: getUpdateAptRndServices(cart.rndServices),
                comment: allComments,
            };
            if (
                hasAppointment ||
                (updated &&
                    ((updated.servicesConfirmed && updated.servicesConfirmed.length > 0) ||
                        (updated.comment && updated.comment.length > 0) ||
                        (updated.maintenanceMenusConfirmed && updated.maintenanceMenusConfirmed.length > 0) ||
                        (updated.rndServices && updated.rndServices.length > 0)))
            ) {
                await updateAppointment(updated);
                props.onProceed();
            }
            else {
                setServiceRequiredError(true);
            }
        }
    };

    const initialValues = {
        categories: availableCategories.findIndex((c) => c.category === ServiceCategory.Common) > -1 ? ServiceCategory.Common : ServiceCategory.All,
        comments: appointment.comment || '',
    };

    // Contents of the comments tab needed to be extracted so the ref is used within its provider
    const CommentsTab = (props: { name: string; }) => {
        const tabFocusRefContext = useTabFocusRefContext();

        return (
            <>
                <h3 className={css.help}>{Localization.IsThereAnythingElseYouWouldLikeToTellUs}</h3>
                <div className={css.textArea}>
                    <GMFormTextArea
                        name={props.name}
                        id={props.name}
                        label={''}
                        title={Localization.TypeAddiitionalComments}
                        ariaLabel={Localization.TypeAddiitionalComments}
                        placeholder={Localization.TypeAddiitionalComments}
                        keyboardLayout={KeyboardLayouts.Alphanumeric}
                        tabFocusRefContext={tabFocusRefContext}
                        autoFocus
                        shiftAfterPeriod
                    />
                </div>
            </>
        );
    };

    return !allServices.length || !menusInitialized || !rndServicesInitialized ? (
        <Spinner />
    ) : (
        <>
            <Formik
                initialValues={initialValues}
                onSubmit={onProceed}
                validationSchema={Yup.object()
                    .shape({
                        [nameof<typeof initialValues>('categories')]: Yup.number()
                            .required()
                            .nullable(),
                    })}
            >
                {({ values }) => (
                    <GMForm
                        submitButton={(
                            <CTAButton
                                styleType={'primary'}
                                type={'submit'}
                                title={Localization.ClickToProceedToNextStep}
                                label={Localization.Proceed}
                                id={Localization.ClickToProceedToNextStep}
                                ariaLabel={Localization.ClickToProceedToNextStep}
                            />
                        )}
                        fullWidthKioskInputs
                    >
                        <Tabs activeTabIndex={activeTabIndex} onTabChange={setActiveTabIndex}>
                            {showMaintenanceScheduleTab && (
                                <Tab
                                    active={activeTabIndex === 0}
                                    tabIndex={0}
                                    label={Localization.MaintenanceSchedules}
                                    hideKeyboard
                                >
                                    <MaintenanceSchedules />
                                </Tab>
                            )}
                            <Tab
                                active={activeTabIndex === (showMaintenanceScheduleTab ? 1 : 0)}
                                tabIndex={showMaintenanceScheduleTab ? 1 : 0}
                                label={Localization.IndividualServices}
                                title={Localization.ClickToViewVehicleServicesOffered}
                                hideKeyboard
                            >
                                <h3 className={css.help}>{Localization.NeedHelpFindingAService}</h3>
                                <div className={css.categories}>
                                    <GMFormSelect id={nameof<typeof values>('categories')} name={nameof<typeof values>('categories')}>
                                        {availableCategories.map(({ category, display }, index) => (
                                            <GMMenuItem key={`${nameof<typeof values>('categories')}-${index}`} value={category}>
                                                {display}
                                            </GMMenuItem>
                                        ))}
                                    </GMFormSelect>
                                </div>
                                <ServiceCardList>{renderServiceItems(values.categories)}</ServiceCardList>
                            </Tab>
                            <Tab
                                active={activeTabIndex === (showMaintenanceScheduleTab ? 2 : 1)}
                                tabIndex={showMaintenanceScheduleTab ? 2 : 1}
                                label={Localization.TypeComments}
                            >
                                <CommentsTab name={nameof<typeof values>('comments')} />
                            </Tab>
                        </Tabs>
                        <Disclaimer />
                    </GMForm>
                )}
            </Formik>
            <GMDialogError
                ariaLabel={Localization.SorryWeNeedMoreDetails}
                open={serviceRequiredError}
                onClose={() => setServiceRequiredError(false)}
                title={Localization.SorryWeNeedMoreDetails}
            >
                {Localization.PleasePickAtLeastOneService}
            </GMDialogError>
        </>
    );
});
